import { createGoodDataServices } from '@mews-framework/good-data/api';
import { createResDiaryServices } from '@mews/business-components';
import { CaseStyle, DateTimePrecision } from '@mews/enums';
import { AppError, createRepositoryRegistryFromFactories, sendRequest } from '@mews/framework';
import { getAppConfiguration } from './appConfiguration';
import {
    accountingExportIntegrationApiBasePath,
    accountingExportIntegrationRestRepositoryFactories,
} from './generated/sdk/accountingExportIntegration/rest';
import { assistantApiBasePath, assistantRestRepositoryFactories } from './generated/sdk/assistant/rest';
import { commanderApiBasePath, commanderRestRepositoryFactories, RestApiOptions } from './generated/sdk/commander/rest';
import { generalApiBasePath, generalRestRepositoryFactories } from './generated/sdk/general/rest';
import { gxApiBasePath, gxRestRepositoryFactories } from './generated/sdk/gx/rest';
import { portfolioApiBasePath, portfolioRestRepositoryFactories } from './generated/sdk/portfolio/rest';
import {
    posMicrosIntegrationApiBasePath,
    posMicrosIntegrationRestRepositoryFactories,
} from './generated/sdk/posMicrosIntegration/rest';

export interface CommonRepositoryParams {
    accessToken?: string;
    client: string;
    enterpriseId: string;
    profileId?: string;
    chainId?: string;
}

// Todo: Will be fixed in JRV-170. Currently, the Atlas server lacks security. To use the repository, we temporarily set withCredentials to false, which will be removed once security is implemented.
const createOptions = ({
    getCommonParams,
    getBaseUrl,
    withCredentials = true,
}: {
    getCommonParams: () => CommonRepositoryParams;
    getBaseUrl: () => string;
    withCredentials?: boolean;
}): RestApiOptions => ({
    request: async (url, params, options) => {
        const baseUrl = getBaseUrl();
        const commonParams = getCommonParams();

        if (!baseUrl || !commonParams) {
            throw new AppError('API params or baseUrl not provided');
        }

        return sendRequest(
            `${baseUrl}${url}`,
            {
                customHeaders: {
                    'X-Accept-Casing': CaseStyle.Camel,
                    'X-Accept-DateTime-Precision': DateTimePrecision.Milliseconds,
                },
                signal: options?.signal,
                credentials: withCredentials ? 'include' : undefined,
            },
            { ...commonParams, ...params }
        );
    },
});

export const getApiBaseUrl = () => getAppConfiguration().platformApiUrl + commanderApiBasePath;

export const getGeneralApiBaseUrl = () => getAppConfiguration().platformApiUrl + generalApiBasePath;

export const createRepositoryRegistry = (getCommonParams: () => CommonRepositoryParams) => {
    const commanderRegistry = createRepositoryRegistryFromFactories(
        commanderRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: getApiBaseUrl,
        })
    );
    const generalRegistry = createRepositoryRegistryFromFactories(
        generalRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: getGeneralApiBaseUrl,
        })
    );
    const portfolioRegistry = createRepositoryRegistryFromFactories(
        portfolioRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: () => getAppConfiguration().platformApiUrl + portfolioApiBasePath,
        })
    );
    const assistantRegistry = createRepositoryRegistryFromFactories(
        assistantRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: () => getAppConfiguration().assistantServiceUrl + assistantApiBasePath,
        })
    );
    const gxRegistry = createRepositoryRegistryFromFactories(
        gxRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: () => getAppConfiguration().guestExperienceRestUrl + gxApiBasePath,
        })
    );
    const posMicrosIntegrationRegistry = createRepositoryRegistryFromFactories(
        posMicrosIntegrationRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: () => getAppConfiguration().posMicrosServiceUrl + posMicrosIntegrationApiBasePath,
            withCredentials: false,
        })
    );
    const accountingExportIntegrationRegistry = createRepositoryRegistryFromFactories(
        accountingExportIntegrationRestRepositoryFactories,
        createOptions({
            getCommonParams,
            getBaseUrl: () => getAppConfiguration().accountingExportServiceUrl + accountingExportIntegrationApiBasePath,
            withCredentials: false,
        })
    );
    const resDiaryRegistry = createResDiaryServices(
        createOptions({
            getCommonParams,
            getBaseUrl: getApiBaseUrl,
        })
    );
    const goodDataRegistry = createGoodDataServices(
        createOptions({
            getCommonParams,
            getBaseUrl: getApiBaseUrl,
        })
    );

    return {
        ...gxRegistry,
        ...assistantRegistry,
        ...commanderRegistry,
        ...generalRegistry,
        ...portfolioRegistry,
        ...resDiaryRegistry,
        ...posMicrosIntegrationRegistry,
        ...accountingExportIntegrationRegistry,
        ...goodDataRegistry,
    };
};
export type RepositoryRegistry = ReturnType<typeof createRepositoryRegistry>;
